import * as React from "react";

import { ChakraComponent, HTMLChakraProps, chakra } from "@chakra-ui/react";
import { FormPrompt, FormPromptWithSaveProps } from "shared/prompt";

import {
  useAuditChartReadOnly,
  useChartTrackingContext,
  useSpeechToTextContext,
} from "contexts";
import { useFormContext } from "react-hook-form";
import { useListChartElements } from "modules/charts-shared/hooks";

/**
 * @todo - fix this type, since chakra.form css prop is incompatible
 * with this type's css property. For now it is extracted from the
 * object top avoid sending it to the form.
 */
type ChartSectionProps = {
  disabled?: boolean;
  sectionId?: string;
  onRouteChangeSave?: FormPromptWithSaveProps["onSave"];
  isPromptSaveLoading?: FormPromptWithSaveProps["isSaveLoading"];
  isDirty?: boolean;
  mainText?: string;
  showSaveBtn?: boolean;
} & HTMLChakraProps<ChakraComponent<"form">>;

function ChartSection(props: ChartSectionProps) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const {
    children,
    css: _css,
    disabled = false,
    sectionId,
    onRouteChangeSave,
    isPromptSaveLoading = false,
    isDirty,
    mainText = "You have unsaved changes in this section!",
    showSaveBtn,
    ...restProps
  } = props;
  const formContext = useFormContext();
  const { isCurrentChartCompleted, updateChartElements } =
    useChartTrackingContext();
  const { isAuditChartReadOnly } = useAuditChartReadOnly();
  const isInteractionLocked = Boolean(
    disabled || isAuditChartReadOnly || isCurrentChartCompleted
  );

  const appendEntriesFromObject = React.useCallback(
    (values: unknown, entries: string[], prefix?: string) => {
      if (!values || typeof values !== "object") {
        return;
      }

      Object.entries(values as Record<string, unknown>).forEach(
        ([key, value]) => {
          if (value === null || value === undefined || value === "") {
            return;
          }

          if (typeof value === "object") {
            appendEntriesFromObject(
              value,
              entries,
              prefix ? `${prefix}.${key}` : key
            );
            return;
          }

          const fieldKey = prefix ? `${prefix}.${key}` : key;
          entries.push(`${fieldKey}: ${String(value)}`);
        }
      );
    },
    []
  );

  const secondaryText = React.useMemo(
    () =>
      ["SHPI", "SROS", "SPEX", "SCHC", "SASS"].includes(sectionId || "")
        ? `You must fill out every subsection
        for this section before the chart can be completed.`
        : "",
    [sectionId]
  );

  const { setCodesList } = useSpeechToTextContext();

  useListChartElements(children, updateChartElements, setCodesList);

  return (
    <>
      {formContext && (
        <FormPrompt
          mainText={mainText}
          title="Warning"
          when={formContext.formState.isDirty && !isInteractionLocked}
          secondaryText={secondaryText}
          showSaveBtn={
            showSaveBtn !== undefined ? showSaveBtn : !!onRouteChangeSave
          }
          isSaveLoading={isPromptSaveLoading}
          onSave={
            onRouteChangeSave
              ? onRouteChangeSave
              : (onComplete) => {
                  onComplete();
                }
          }
        />
      )}
      <chakra.fieldset
        disabled={isInteractionLocked}
        height="100%"
        width="100%"
        pointerEvents={isInteractionLocked ? "none" : undefined}
      >
        <chakra.form
          padding="1.5rem"
          sx={{ "> :not(:last-child)": { marginBottom: "1rem" } }}
          {...restProps}
        >
          {children}
        </chakra.form>
      </chakra.fieldset>
    </>
  );
}

export type { ChartSectionProps };

export { ChartSection };
